import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { motion } from "framer-motion";

import ActivityList from "@/features/activity/ActivityList";
import { Arena } from "@/features/arena/Arena";
import MultiplierConfigPanel from "@/features/config/MultiplierConfigPanel";
import TVLeaderboard from "@/features/leaderboard/TVLeaderboard";
import PlayerList from "@/features/player/PlayerList";
import { RoomPicker } from "@/features/room/RoomPicker";
import { getStoredRoom } from "@/features/room/roomStorage";
import RatingChart from "@/features/simulation/RatingChart";
import { I18nProvider, useTranslations } from "@/i18n";
import { exportState, importStateFn, useStore } from "@/store/store";
import { SyncEngine } from "@/store/SyncEngine";
import { PageShell } from "@/ui/PageShell";
import { ActionRow, PillBtn, PillBtnDanger } from "@/ui/styled";

import Simulation from "./features/simulation/Simulation";


const ResetScoresButton = () => {
  const matches = useStore((s) => s.matches);
  const resetScores = useStore((s) => s.resetScores);
  const { t } = useTranslations();
  const matchCount = Object.keys(matches).length;

  if (matchCount === 0) return null;

  return (
    <ActionRow>
      <PillBtnDanger
        onClick={() => {
          if (window.confirm(t("data.resetScoresConfirm"))) {
            resetScores();
          }
        }}
      >
        {t("data.resetScores")}
      </PillBtnDanger>
    </ActionRow>
  );
};

const DataBar = () => {
  const importStateAction = useStore((s) => s.importState);
  const resetStateAction = useStore((s) => s.resetState);
  const { t } = useTranslations();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = useCallback(() => {
    const json = exportState();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `de-vurige-stand-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleImport = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const imported = importStateFn(reader.result as string);
          importStateAction(imported);
        } catch {
          alert(t("data.importError"));
        }
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [importStateAction, t],
  );

  const handleReset = useCallback(() => {
    if (window.confirm(t("data.resetConfirm"))) {
      resetStateAction();
    }
  }, [resetStateAction, t]);

  return (
    <ActionRow>
      <PillBtn onClick={handleExport}>{t("data.export")}</PillBtn>
      <PillBtn onClick={() => fileInputRef.current?.click()}>{t("data.import")}</PillBtn>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImport}
        style={{ display: "none" }}
      />
      <PillBtnDanger onClick={handleReset}>{t("data.reset")}</PillBtnDanger>
    </ActionRow>
  );
};

// ── Animated section wrapper ───────────────────────────────────

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const } },
};

const AnimatedSection = ({ children }: { children: React.ReactNode }) => (
  <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: "contents" }}>
    {children}
  </motion.div>
);

const Card = ({ children }: { children: React.ReactNode }) => (
  <motion.div variants={fadeUp}>{children}</motion.div>
);

// ── Route content ──────────────────────────────────────────────

const OrganizerPage = () => {
  const { t } = useTranslations();
  return (
    <PageShell icon="📋" title={t("nav.organize")}>
      <AnimatedSection>
        <Card>
          <DataBar />
        </Card>
        <Card>
          <ActivityList />
        </Card>
        <Card>
          <PlayerList />
        </Card>
      </AnimatedSection>
    </PageShell>
  );
};

const AdminPage = () => {
  const { t } = useTranslations();
  return (
    <PageShell icon="⚙️" title={t("nav.admin")} wide>
      <AnimatedSection>
        <Card>
          <DataBar />
        </Card>
        <Card>
          <ResetScoresButton />
        </Card>
        <Card>
          <MultiplierConfigPanel />
        </Card>
        <Card>
          <RatingChart />
        </Card>
        <Card>
          <Simulation />
        </Card>
      </AnimatedSection>
    </PageShell>
  );
};

export default function App() {
  const [room, setRoom] = useState<string | null>(getStoredRoom);

  if (!room) {
    return (
      <I18nProvider>
        <RoomPicker onJoin={setRoom} />
      </I18nProvider>
    );
  }

  return (
    <I18nProvider>
      <SyncEngine room={room} />
      <Routes>
        <Route path="/" element={<Arena />} />
        <Route path="/organizer" element={<OrganizerPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/tv" element={<TVLeaderboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </I18nProvider>
  );
}